import { useState, useMemo } from 'react'
import { useAdminMoney } from '@/hooks/useMoney'

/** Tenant-currency formatters for the analytics tabs (full amounts, and short form for chart axes). */
export function useAnalyticsMoney() {
  const { fmt } = useAdminMoney()
  return {
    fmtMoney: (n: number) => fmt(n),
    fmtMoneyShort: (n: number) => fmt(n, { compact: true }),
  }
}

export function fmtRating(r: number | null) {
  return r == null ? '—' : r.toFixed(1)
}

export const CHART_COLORS = ['#6366f1', '#8b5cf6', '#a78bfa', '#c4b5fd', '#818cf8', '#4f46e5', '#7c3aed', '#5b21b6']

type SortDir = 'asc' | 'desc'

export function useSortable<T>(data: T[], defaultKey: keyof T) {
  const [key, setKey] = useState<keyof T>(defaultKey)
  const [dir, setDir] = useState<SortDir>('desc')

  const sorted = useMemo(() => [...data].sort((a, b) => {
    const av = a[key], bv = b[key]
    if (av == null && bv == null) return 0
    if (av == null) return 1
    if (bv == null) return -1
    if (typeof av === 'number' && typeof bv === 'number') return dir === 'asc' ? av - bv : bv - av
    return dir === 'asc'
      ? String(av).localeCompare(String(bv))
      : String(bv).localeCompare(String(av))
  }), [data, key, dir])

  const toggle = (k: keyof T) => {
    if (k === key) setDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setKey(k); setDir('desc') }
  }

  return { sorted, toggle, sortKey: key, sortDir: dir }
}
